import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/all";
import MediaQueries from "../_core/_mediaQueries";
gsap.registerPlugin(ScrollTrigger);

/*
    @param {settings}
    Un oggetto che contiene:
        - duration: La durata dell'animazione (in secondi)
        - distance: La distanza per cui si muoverà l'animazione (in px)
        - start: La distanza (in percentuale) dal top del trigger dell'animazione (in genere ciò che verra animato)
        - triggerDistance: La distanza (in percentuale) dello schermo
        - stagger: La durata della pausa fra l'inizio di una animazione e l'altra
        - staggerFrom: Da dove parte lo stagger
        - staggerGrid: In caso gli elementi da staggerare siano in una griglia (valori possibili sono auto oppure righe,colonne)
        - staggerAxis: Utile solo se gli elementi sono in una griglia, permette di specificare l'asse in cui si muoverà lo stagger (valori possibili sono x o y, https://codepen.io/GreenSock/pen/rNVVGOa per capire meglio)
        - staggerEase: L'ease applicato allo stagger (nel link del punto prima si può vedere anche questo)
        - ease: L'ease dell'animazione

    Se questi parametri non sono presenti ci sono dei valori di base, questi valori possono essere overridati anche dentro l'html


    @usage
    Per usare inserire i parametri dentro un tag html:
        - animate: (up, down, left, right, up-left, down-right, ecc.) Indica la direzione dell'animazione. Se lasciato vuoto l'animazione sarà solamente un fade-in senza movimento
                    mirror è una animazione speciale che fa entrare i figli da sinistra e destra (pari da destra e dispari da sinistra) (esiste mirrorreverse)
                    updown è una animazione speciale che fa entrare i figli da sopra e da sotto (pari da sopra e dispari da sotto) (esiste downup)
                    in entrambi i casi non è necessario usare animate-children
        - animate-ignore: Inserito in un figlio di un elemento con animate-children, indica che questo figlio non dovrebbe essere animato
        - animate-property (nel caso di dueParole diventa due-parole): Valida per tutte le proprietà descritte in @param. È un override dei parametri di base specifico del tag in cui è inserito.
        - animate-children: Applica l'animazione ai children del tag. Con questa proprietà lo stagger verrà applicato, se animate="mirror", non è necessario
        - animate-from-parent: (int) Indica il numero di volte di risalita del DOM per l'inserimento del trigger (ad esempio se vogliamo dare una animazione a dei figli ma il trigger deve essere il padre)
        - animate-reset: Applica un secondo scrollTrigger che resetta l'animazione una volta che si ritorna indietro del tutto e non è più nello schermo

        Caso speciale: È possibile fare l'override di una direzione data dal padre:

        <div animate="mirror">
            <div></div>
            <div animate="left"></div>
            <div></div>
            <div></div>
        </div>

        In questo caso l'animazione normale dovrebbe essere l r l r, ma grazie all'override del figlio diventa l l l r

    

    @snippet
    Snippet da inserire in vscode:
        - animate: Solo le cose di base
        - animateMore: Aggiunge alcune cose
        - animateFull: Se serve cambiare quasi tutto

    Da mettere nel file html.json, se messe nel file snippets globale bisogna precisare lo scope

    "animate": {
		"prefix": "animate",
		"body": [
			"animate='${1|left,right,mirror,up,down,up-left,up-right,down-left,down-right, |}'${2| animate-children, |}${3| animate-reset , |}",
		],
		"description": "Animations"
	},
	
	"animateMore": {
		"prefix": "animateMore",
		"body": [
			"animate='${1|left,right,mirror,up,down,up-left,up-right,down-left,down-right, |}'${2| animate-children , |}${3|animate-reset, |}animate-duration='${3|0.5,0.25,0.75,1|}' animate-distance='${4|75,25,50,100|}' animate-stagger='${5|0.5,0.1,0.2,0.3,0.75,1|}'",
		],
		"description": "Animations con i parametri semplici"
	},
	
	"animateFull": {
		"prefix": "animateFull",
		"body": [
			"animate='${1|left,right,mirror,up,down,up-left,up-right,down-left,down-right, |}'${2| animate-children , |}${3|animate-reset, |}animate-from-parent='${4|0,1,2,3|}' animate-duration='${3|0.5,0.25,0.75,1|}' animate-distance='${4|75,25,50,100|}' animate-stagger='${5|0.5,0.1,0.2,0.3,0.75,1|}' animate-stagger-grid='${6:auto}' animate-stagger-axis='${7|x,y|}' animate-stagger-ease='${8|power1,power2,power3,power4,expo,circ,sine,bounce,elastic|}.${9|in,out,inOut|}}' animate-start='${10|50,25,75,100|}' animate-trigger-distance='${11|50,25,75,100|}' animate-ease='${12|power1,power2,power3,power4,expo,circ,sine,bounce,elastic|}.${13|in,out,inOut|}'",
		],
		"description": "Animations con tutti i parametri"
	},

    @Note importanti: Agli elementi che dovranno entrare è importante che vengano impostate transition specifiche, poichè con transition: all, si rompe il movimento, o comunque fare in modo che non abbiano una transizione applicata a transform e opacity

*/

export interface Settings {
	/** La durata dell'animazione (in secondi) */
	duration?: number; // default: 1

	/** La distanza per cui si muoverà l'animazione (in px) */
	distance?: number; // default: 70

	/**
	 * La distanza (in percentuale) dal top del trigger dell'animazione
	 * (in genere ciò che verrà animato)
	 */
	start?: number; // default: 25

	/** La distanza (in percentuale) dello schermo */
	triggerDistance?: number; // default: 98

	/** La durata della pausa fra l'inizio di una animazione e l'altra */
	stagger?: number; // default: 0.5

	/** Da dove parte lo stagger */
	staggerFrom?: number | "start" | "center" | "end" | "edges" | "random" | [number, number]; // default: "start"

	/**
	 * In caso gli elementi da staggerare siano in una griglia
	 * (valori possibili: "auto", "righe", "colonne")
	 */
	staggerGrid?: "auto" | [number, number];

	/**
	 * Utile solo se gli elementi sono in una griglia,
	 * permette di specificare l'asse in cui si muoverà lo stagger
	 * (valori possibili: "x" | "y")
	 * https://codepen.io/GreenSock/pen/rNVVGOa
	 */
	staggerAxis?: "x" | "y";

	/**
	 * L'ease applicato allo stagger
	 * (nel link del punto prima si può vedere anche questo)
	 */
	staggerEase?: gsap.EaseString;

	/** L'ease dell'animazione */
	ease?: gsap.EaseString; // default: "power2.out"
}

interface AnimateData {
	/** Il target dell'animazione */
	target: Element | Element[] | null;

	/** La direzione sull'asse x (default: 0) */
	dirX?: number | number[];

	/** La direzione sull'asse y (default: 0) */
	dirY?: number | number[];

	/** Se l'animazione è di tipo mirror (default: false) */
	mirror?: boolean;

	/** Se l'animazione è sui children (e quindi applica lo stagger) (default: false) */
	animateChildren?: boolean;

	/** Di quanti parent risalire per mettere il trigger (default: 0) */
	parents?: number;

	/** Se l'animazione si resetta allo scroll up (default: false) */
	reset?: boolean;

	/** I settings dell'animazione */
	settings?: Settings;
}

const _animations = (() => {
	const defaultSettings: Settings = {
		duration: 1,
		distance: 70,
		start: 25,
		triggerDistance: 98,
		stagger: 0.2,
		staggerFrom: "start",
		staggerGrid: undefined,
		staggerAxis: undefined,
		staggerEase: undefined,
		ease: "power2.out",
	};

	const init = (settings: Settings | undefined) => {
		// Se l'utente ha reduce motion attivo, togliamo tutte le animazione
		if (MediaQueries.reduceMotion) return;

		if (settings) {
			if (settings.duration != undefined) {
				defaultSettings.duration = settings.duration;
			}
			if (settings.distance != undefined) {
				defaultSettings.distance = settings.distance;
			}
			if (settings.triggerDistance != undefined) {
				defaultSettings.triggerDistance = settings.triggerDistance;
			}
			if (settings.stagger != undefined) {
				defaultSettings.stagger = settings.stagger;
			}
			if (settings.staggerFrom != undefined) {
				defaultSettings.staggerFrom = settings.staggerFrom;
			}
			if (settings.staggerGrid != undefined) {
				defaultSettings.staggerGrid = settings.staggerGrid;
			}
			if (settings.staggerAxis != undefined) {
				defaultSettings.staggerAxis = settings.staggerAxis;
			}
			if (settings.staggerEase != undefined) {
				defaultSettings.staggerEase = settings.staggerEase;
			}
			if (settings.ease != undefined) {
				defaultSettings.ease = settings.ease;
			}
			if (settings.start != undefined) {
				defaultSettings.start = settings.start;
			}
		}

		document.querySelectorAll("[animate]").forEach((element) => {
			let animateData = getAnimateData(element, undefined, undefined);

			if (element.getAttribute("animated") == null) animate(animateData);
		});
	};

	function resetter(element: Element | Element[], animation: GSAPAnimation) {
		ScrollTrigger.create({
			trigger: element,
			start: "top bottom",
			onLeaveBack: () => animation.pause(0),
			markers: true,
		});
	}

	const getAnimateData = (target: Element, override: AnimateData | undefined, parentUp: boolean | undefined) => {
		let animateData: AnimateData = {
			target: target,
			dirX: [0],
			dirY: [0],
			mirror: false,
			animateChildren: false,
			parents: 0,
			reset: false,
			settings: cloneSettings(defaultSettings),
		};

		if (override != undefined) {
			animateData = cloneData(override);
			animateData.target = target;
		}

		if (parentUp === true) {
			animateData.parents ??= 0;
			animateData.parents++;
		}

		let data = target.getAttribute("animate-duration");
		if (data != null) {
			animateData.settings!.duration = parseFloat(data) ?? 0;
		}

		data = target.getAttribute("animate-distance");
		if (data != null) {
			animateData.settings!.distance = parseInt(data) ?? 0;
		}

		data = target.getAttribute("animate-start");
		if (data != null) {
			animateData.settings!.start = parseInt(data) ?? 0;
		}

		data = target.getAttribute("animate-trigger-distance");
		if (data != null) {
			animateData.settings!.triggerDistance = parseInt(data) ?? 0;
		}

		data = target.getAttribute("animate-stagger");
		if (data != null) {
			animateData.settings!.stagger = parseFloat(data) ?? 0;
		}

		data = target.getAttribute("animate-stagger-from");
		if (data != null) {
			// @ts-ignore
			animateData.settings!.staggerFrom = data;
		}

		data = target.getAttribute("animate-stagger-grid");
		if (data != null) {
			const string = data;

			if (string != undefined && string != "auto") {
				let lengths = string.split(",");
				if (lengths.length == 2) {
					animateData.settings!.staggerGrid = [Number(lengths[0]), Number(lengths[1])];
				}
			} else {
				animateData.settings!.staggerGrid = string;
			}
		}

		data = target.getAttribute("animate-stagger-axis");
		if (data != null) {
			if (data === "x" || data === "y") animateData.settings!.staggerAxis = data;
			else animateData.settings!.staggerAxis = "x";
		}

		data = target.getAttribute("animate-stagger-ease");
		if (data != null) {
			animateData.settings!.staggerEase = data;
		}

		data = target.getAttribute("animate-ease");
		if (data != null) {
			animateData.settings!.ease = data;
		}

		switch (target.getAttribute("animate")) {
			case "up":
				animateData.dirY = [1];
				break;

			case "down":
				animateData.dirY = [-1];
				break;

			case "left":
				animateData.dirX = [1];
				break;

			case "right":
				animateData.dirX = [-1];
				break;

			case "up-left":
				animateData.dirY = [1];
				animateData.dirX = [1];
				break;

			case "up-right":
				animateData.dirY = [1];
				animateData.dirX = [-1];
				break;

			case "down-left":
				animateData.dirY = [-1];
				animateData.dirX = [1];
				break;

			case "down-right":
				animateData.dirY = [-1];
				animateData.dirX = [-1];
				break;

			case "mirror":
				animateData.mirror = true;
				animateData.dirY = 1;
				animateData.dirX = 1;
				break;

			case "mirrorreverse":
				animateData.mirror = true;
				animateData.dirY = 1;
				animateData.dirX = -1;
				break;

			case "updown":
				animateData.mirror = true;
				animateData.dirY = -1;
				animateData.dirX = 1;
				break;

			case "downup":
				animateData.mirror = true;
				animateData.dirY = -1;
				animateData.dirX = -1;
				break;

			default:
				break;
		}

		animateData.animateChildren =
			target.getAttribute("animate-children") != null ? target.getAttribute("animate-children") != null : animateData.animateChildren;

		data = target.getAttribute("animate-from-parent");
		if (data != null) {
			animateData.parents = parseInt(data) ?? 0;
		}

		animateData.reset = target.getAttribute("animate-reset") != null ? target.getAttribute("animate-reset") != null : animateData.reset;

		return animateData;
	};

	const cloneSettings = (settings: Settings) => {
		return { ...settings } as Settings;
	};

	const cloneData = (animData: AnimateData) => {
		const ret = { ...animData };
		if (animData.settings) ret.settings = cloneSettings(animData.settings);
		return ret;
	};

	function animate(animateData: AnimateData) {
		if (!animateData.target) return;

		if (animateData.mirror) {
			const children = (animateData.target as Element).children;
			animateData.mirror = false;
			animateData.animateChildren = false;
			let skipped = 0;

			// dirY = 1 -> mirror (dirX per normale o reverse)
			// dirY = -1 -> updown (dirX per normale o reverse)

			if (animateData.dirY == 1) {
				// Mirror

				const reversed = animateData.dirX == -1;
				const actualChilds: Element[] = [];

				animateData.dirY = [0];
				animateData.dirX = reversed ? [1, -1] : [-1, 1];

				for (let i = 0; i < children.length; i++) {
					if (children.item(i)?.getAttribute("animate-ignore") != null) {
						skipped++;
						continue;
					} else {
						const child = children.item(i);
						if (child) actualChilds.push(child);
					}
				}

				animateData.target = actualChilds;

				animate(animateData);
			} else if (animateData.dirY == -1) {
				// UpDown

				const reversed = animateData.dirX == -1;
				const actualChilds = [];

				animateData.dirX = [0];
				animateData.dirY = reversed ? [-1, 1] : [1, -1];

				for (let i = 0; i < children.length; i++) {
					if (children.item(i)?.getAttribute("animate-ignore") != null) {
						skipped++;
						continue;
					} else {
						const child = children.item(i);
						if (child) actualChilds.push(child);
					}
				}

				animateData.target = actualChilds;

				animate(animateData);
			} else {
				console.warn("dirY invalido");
			}
		} else {
			let trigger = animateData.target;

			if (animateData.parents ?? 0 > 0) {
				for (let i = 0; i < animateData.parents!; i++) {
					trigger = (trigger as Element).parentElement as Element;
				}
			}

			if (animateData.dirX != 0) {
                let element = animateData.target;

                // Se viene usato mirror o updown, il target sarà una lista di elementi fratelli fra loro, quindi prendiamo il primo
                if (element instanceof Array) {
                    element = element[0];
                }

                while (!element.parentElement?.classList.contains("umb-block-list") && element.parentElement) {
                    element = element.parentElement;
                }

                if (element.parentElement?.classList.contains("umb-block-list")) {
                    gsap.set(element, {
                        overflowX: "hidden",
                    });
                } else {
                    if (Array.isArray(animateData.target)) {
                        const targets = animateData.target as Element[];
                        gsap.set(targets[0]?.parentElement, {
                            overflowX: "hidden",
                        });
                    } else {
                        gsap.set(animateData.target.parentElement, {
                            overflowX: "hidden",
                        });
                    }
                }
            }

			const target = animateData.animateChildren ? (animateData.target as Element).children : animateData.target;

			const animation = gsap.fromTo(
				target,
				{
					opacity: 0,
					x: (index, trgt) => {
						const trgtAnim = getAnimateData(trgt, animateData, undefined);
						return trgtAnim.settings!.distance! * gsap.utils.wrap((trgtAnim.dirX as number[]), index);
					},
					y: (index, trgt) => {
						const trgtAnim = getAnimateData(trgt, animateData, undefined);
						return trgtAnim.settings!.distance! * gsap.utils.wrap((trgtAnim.dirY as number[]), index);
					},
				},
				{
					opacity: 1,
					x: 0,
					y: 0,
					duration: animateData.settings!.duration,
					ease: animateData.settings!.ease,
					stagger: {
						each: animateData.settings!.stagger,
                        from: animateData.settings!.staggerFrom,
						grid: animateData.settings!.staggerGrid,
						axis: animateData.settings!.staggerAxis,
						ease: animateData.settings!.staggerEase,
					},
					scrollTrigger: {
						trigger: trigger,
						start: `${animateData.settings!.start}% ${animateData.settings!.triggerDistance}%`,
						once: !animateData.reset,
						// markers: true,
					},
					onComplete: () => {
						if (target instanceof Array || target instanceof HTMLCollection) {
							Array.from(target).forEach((trgt) => {
								trgt.setAttribute("animated", "");
							});
						} else {
							target.setAttribute("animated", "");
						}
					},
				}
			);

			if (animateData.reset) resetter(trigger, animation);
		}
	}

	return {
		init: init,
	};
})();

export default _animations;
